import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {Radio,FormControl,RadioGroup, FormControlLabel} from '@mui/material';
import postData from './postData';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz } from 'ag-grid-community'
import { purpleairStatus, purpleairColor } from './purpleairfunctions';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText, Box, Typography } from '@mui/material';

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea } from 'recharts';


const PurpleairWebpageIncidentSiteSpecific = () => {
  //arrays to hold sensor readings
  const [aryIncident, setIncident] = useState(undefined);
  const [retryCount, setRetryCount] = useState(0);

  //warning dialog variables
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  //variables for graphing
  const [aryGraph, setGraphArray] = useState(undefined);
  const [maxY, setMaxY] = useState(0);
  const [filterValue, setFilterValue] = useState(0);

  //-------------------------------------------------------------
  //  grid variables
  const gridRef = useRef();
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);

  function statusValueGetter(params) {
    return purpleairStatus(params.data.max25);
  }

  const formatDate = (params) => {
    let thedate = new Date(params.data.datetimestart);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(thedate);
  };

  function statusColor(params) {
    let bgcolor = purpleairColor(params.data.max25);
    return { backgroundColor: bgcolor }
  }

  function diffMinutes(params) {
    let dateStart = new Date(params.data.datetimestart);
    let dateEnd = new Date(params.data.datetimeend);
    const diffInMs = dateEnd - dateStart; // difference in milliseconds
    if (diffInMs < 0) { return 'current conditions' };
    let numMin = Math.round(diffInMs / (1000 * 60)); // convert ms to minute
    if (numMin > 60) {
      let numHour = Math.trunc(numMin / 60);
      let remainMin = numMin - numHour * 60;
      let strResult = `${numHour} hour`;
      if (numHour > 1) { strResult = strResult + 's' };
      if (remainMin > 0) {
        strResult = strResult + ` and ${remainMin} minutes`;
      }
      return strResult
    } else {

      return `${numMin} minutes`
    } //else > 60;
  }

  const columnDefs = [
    { headerName: 'Beginning on', valueGetter: formatDate, width: 270 }
    , { headerName: 'Spike Risk Level', valueGetter: statusValueGetter, cellStyle: statusColor }
    , { headerName: 'Duration', valueGetter: diffMinutes }
    , { headerName: 'US Recycling', field: 'max25' }
    , { headerName: 'City-wide', field: 'reference25' }
    , { headerName: 'US Recycling Sensor', field: 'sensorname' }
  ];

  const defaultColDef = {
    sortable: true,
    autoHeight: true,
    resizable: true,
    suppressMovable: true
  };

  const gridTheme = themeQuartz.withParams({
    fontSize: 12
  })

  const rowSelection = useMemo(() => {
    return { mode: "singleRow" };
  }, []);

  const popupParent = useMemo(() => {
    return document.body;
  }, []);

  const onBtnExport = useCallback(() => {
    gridRef.current.api.exportDataAsCsv();
  }, []);


  const autoSizeStrategy = useMemo(() => ({
    type: 'fitCellContents'
  }), []);

  const onSelectionChanged = useCallback((event) => {
    const selectedRows = event.api.getSelectedRows();
    setMaxY(0);
    if (selectedRows.length > 0) {
      const selectedRow = selectedRows[0];
      getGraphdata(selectedRow);
    }
  }, []);

  //-------------------------------------------------------------
  // ----- handle user warning
  useEffect(() => {
    if (errorMessage !== '') {
      setOpenWarningDialog(true);
    }
  }, [errorMessage]
  )
  const handleErrorDialogOK = () => {
    setOpenWarningDialog(false);
    setErrorMessage('');
  };

  //-------------------------------------------------------------  
  // retrieve relevant submissions ------------------------------
  const getIncidents = async (optionalFilter) => {

    const formData = new FormData();
    if (optionalFilter === undefined) {
      formData.append('afterhourFlag', 0);
    } else {
      formData.append('afterhourFlag', optionalFilter);
    }
    var objEntity = await postData('https://sarasotapublic.com/usrecycling/purpleairWebpageIncidentSiteSpecific.php', formData);
    setErrorMessage(objEntity.theError);
    if (objEntity.theData !== null) {
      setIncident(objEntity.theData);
    } else {
      if (retryCount < 3) {
        // Retry after 3 seconds
        setTimeout(() => setRetryCount(retryCount + 1), 3000);
      } //retry
    } //else 
  }

  useEffect(() => {
    getIncidents();
    // eslint-disable-next-line
  }, [retryCount]);

  //-------------------------------------------------------------  
  // retrieve relevant submissions ------------------------------
  const getGraphdata = async (theRow) => {

    const formData = new FormData();
    formData.append('start', theRow.datetimestart);
    formData.append('end', theRow.datetimeend);
    var objEntity = await postData('https://sarasotapublic.com/usrecycling/purpleairWebpageIncidentGraph.php', formData);
    setErrorMessage(objEntity.theError);
    if (objEntity.theData !== null) {
      setGraphArray(objEntity.theData);
    }
  } 



  const handleFilterChange = (event) => {
    setFilterValue(event.target.value);
    setIncident(undefined);
    setGraphArray(undefined); 
    getIncidents(event.target.value);
  };




  //-------------------------------------------------------------
  return (
    <><div style={{ textAlign: 'left' }}>
      <Box display="flex" alignItems="center" gap={10}>
        <Typography variant="h5" gutterBottom>
          Hazardous operations at US Recycling
        </Typography>
            <FormControl>
          <RadioGroup
            row
            value={filterValue}
            onChange={handleFilterChange}
          >
            <FormControlLabel value="0" sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: "0.8em",
              }
            }} control={<Radio />} label="all" />
            <FormControlLabel value="1" sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: "0.8em",
              }
            }}
            control={<Radio />} label="only after hours" />
          </RadioGroup>
    </FormControl>

        <Button variant='contained' onClick={onBtnExport} >Export</Button>
        <span style={{ fontSize: '0.8em', fontStyle: 'italic' }}>reference sensor "SCG Air & Water Quality_EPA"
          <br/>values of PM 2.5 ( µg/m<sub>3</sub> ) taken on a two minute inteval -- reporting began on June 22, 2025
        </span>
      </Box>
      <div id="gridContainer" style={{ height: '35vh', width: '100%', textAlign: 'left' }}>
        {(aryIncident !== undefined?
        <div style={gridStyle} className="ag-theme-alpine">
          <AgGridReact
            ref={gridRef}
            rowData={aryIncident}
            suppressExcelExport={true}
            popupParent={popupParent}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            theme={gridTheme}
            autoSizeStrategy={autoSizeStrategy}
            rowSelection={rowSelection}
            onSelectionChanged={onSelectionChanged}
          />
        </div>
        :           <div style={{
              margin: '20px 10px', display: 'flex', height: '35v', gap: '20px', padding: '10px'
              , justifyContent: 'center', alignItems: 'center'
            }}>
              <Typography variant="h6"> Loading incidents...
              </Typography>
            </div>
        )}
        <div>
          {aryGraph === undefined ? (
            <div style={{
              margin: '20px 10px', display: 'flex', height: '20vh', background: '#F0F0F0', gap: '20px', padding: '10px'
              , justifyContent: 'center', alignItems: 'center'
            }}>
            { aryIncident !== undefined &&
              <Typography variant="h6"> Select an incident to show air quality graph
              </Typography>
              }
            </div>
          ) : (
            <div style={{
              margin: '20px 10px', display: 'flex', height: '35vh', gap: '20px', padding: '10px'
              , justifyContent: 'center', alignItems: 'center'
            }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={aryGraph}>
                  <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
                  <XAxis dataKey="theTime" />
                  <YAxis
                    domain={['auto', 'auto']}
                    tick={({ x, y, payload }) => {
                      // Logs each tick value -- works most of the time
                      if (payload.value > maxY) {
                        setMaxY(payload.value);
                      }
                      return (
                        <text x={x} y={y} dy={4} textAnchor="end" fill="#666">
                          {payload.value}
                        </text>
                      );
                    }}
                  />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" name=" US Recycling" dataKey="siteValue" stroke="red" strokeWidth={2} />
                  <Line type="monotone" name="city-wide" dataKey="refValue" stroke="green" />

                  <ReferenceArea
                    y1={9}
                    y2={maxY}
                    fill='#F0E26F'
                    strokeOpacity={0}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
      <Dialog
        open={openWarningDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Submission List Error
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {errorMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleErrorDialogOK}>Ok</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PurpleairWebpageIncidentSiteSpecific;